"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import {
  Leaf,
  ShieldCheck,
  Recycle,
  UserCog,
  TreePine,
  Heart,
  Users,
  Sparkles,
  LucideIcon,
  type LucideProps,
} from "lucide-react";

type Accion = {
  key: string;
  title: string;
  description: string;
};

type Campana = {
  key: string;
  title: string;
};

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/SIGAT/321A7965.jpg";

const AMBIENTAL_ICONS: Record<string, LucideIcon> = {
  recursos: Leaf,
  residuos: Recycle,
  capacitacion: UserCog,
  reforestacion: TreePine,
  educacion: Users,
};

const CAMPANA_ICONS: Record<string, LucideIcon> = {
  vih: Heart,
  humo: ShieldCheck,
  dengue: ShieldCheck,
  vacunacion: Sparkles,
};

export default function ResponsabilidadPage() {
  const t = useTranslations("responsabilidadPage");
  const intro = t.raw("intro") as string[];
  const ambientales = t.raw("ambientales") as Accion[];
  const campanas = t.raw("campanas") as Campana[];

  return (
    <main className="min-h-screen bg-white">
      {/* Hero con imagen */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src={HERO_IMAGE}
          alt={t("title")}
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px bg-white" />
              {t("eyebrow")}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
              {t("title")}
            </h1>
            <p className="text-white/80 text-base md:text-lg leading-relaxed">
              {t("subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-[80%] mx-auto py-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-12 items-start">
          <div className="max-w-3xl space-y-5">
            {intro.map((p, i) => (
              <p
                key={i}
                className="text-gray-600 text-base md:text-lg leading-relaxed"
              >
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Acciones ambientales */}
      <section className="max-w-[80%] mx-auto pb-24">
        <div className="max-w-3xl mb-14">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#102542] font-semibold mb-4">
            <span className="w-8 h-px bg-[#102542]" />
            {t("ambientalesEyebrow")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
            {t("ambientalesTitle")}
          </h2>
          <p className="text-gray-600 text-base leading-relaxed">
            {t("ambientalesSubtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
          {ambientales.map((item) => {
            const Icon: React.ComponentType<LucideProps> =
              AMBIENTAL_ICONS[item.key] ?? Leaf;
            return (
              <article
                key={item.key}
                className="bg-white p-8 flex flex-col transition-colors hover:bg-gray-50"
              >
                <Icon className="w-6 h-6 text-[#102542] mb-5" />
                <h3 className="text-base font-semibold text-gray-900 mb-3 tracking-tight">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Campañas */}
      <section className="max-w-[80%] mx-auto pb-24">
        <div className="max-w-3xl mb-14">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#102542] font-semibold mb-4">
            <span className="w-8 h-px bg-[#102542]" />
            {t("campanasEyebrow")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight">
            {t("campanasTitle")}
          </h2>
          <p className="text-gray-600 text-base leading-relaxed">
            {t("campanasSubtitle")}
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
          {campanas.map((camp) => {
            const Icon: React.ComponentType<LucideProps> =
              CAMPANA_ICONS[camp.key] ?? Heart;
            return (
              <article
                key={camp.key}
                className="bg-white p-8 flex flex-col items-start min-h-[160px] transition-colors hover:bg-gray-50"
              >
                <Icon className="w-6 h-6 text-[#102542] mb-5" />
                <p className="text-sm font-semibold text-gray-900 leading-snug">
                  {camp.title}
                </p>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
}
